<template>
  <div class="users">
    <h1 class="blue-color">{{$t("organizations.users")}}</h1>
    <div v-if="hasNoUsers" class="no-users">
      <invite-users
        v-on="$listeners"
        :noValuesLabel="$t('organizations.invite_users.subtitle')"
      />
    </div>
    <div v-else class="user-list">
      <el-form class="search-form">
        <elsa-search
          v-model="queryString"
          @search="searchUsers"
        />
      </el-form>
      <div class="actions divider">
        <div class="options-wrapper">
        <div class="options">
          <el-collapse accordion>
            <el-collapse-item name="1">
              <template slot="title">
                <div class="button">
                  <i class="el-icon-circle-plus-outline"></i>
                  <span> {{$t("organizations.users_section.invite_users")}}</span>
                </div>
              </template>

              <span class="action-item invite-users" @click="onClickContinue('email-invite')">
                <i class="elsa-icon-email" />
                {{ $t("organizations.users_section.invite_by_email") }}
              </span>
              <span
                class="action-item import-users"
                @click="onClickContinue('import-users')"
              >
                <i class="elsa-icon-import" />
                {{ $t("organizations.users_section.import_users") }}
              </span>
              <!-- <span
                class="action-item share-link"
                @click="onClickContinue('share-link')"
              >
                <i class="elsa-icon-share" />
                {{ $t("organizations.users_section.share_link") }}
              </span> -->
            </el-collapse-item>
          </el-collapse>
        </div>
        <div v-if="showUserOptions" class="users-options">
            <more-options-popover
              placement="bottom-end"
              width="225"
              trigger="click"
              :visible-arrow="true"
              :displayData="getOrganizationClasses"
              :actionFunction="addToGroup"
            >
              <template v-slot:moreOption>
                <span
                  @click="handleAddGroupClick"
                >
                  <i class="el-icon-circle-plus-outline"/>
                  {{ $t($i18nLabelsHelper(organizationData.type, 'add_to_group')) }}
                </span>
              </template>

              <chip-icon
                :tooltip="$t($i18nLabelsHelper(organizationData.type, 'add_to_group_title'))"
                icon="elsa-icon-add-to-class"
              />
            </more-options-popover>
            <chip-icon
              @click.native="modals.showRemoveAccountModal = true;"
              :tooltip="$t($i18nLabelsHelper(organizationData.type, 'remove_student_from_organization'))"
              icon="elsa-icon-remove-student"
            />
          <!-- <span>
            <chip-icon
              icon="elsa-icon-revoke-license"
              :tooltip="$t('organizations.users_section.revoke_license')" @click.native="showRevokeLicense"
            />
          </span>
          <span>
            <chip-icon
              icon="elsa-icon-delete"
              :tooltip="$t('organizations.users_section.delete')" @click.native="modals.showRemoveAccountModal=true"
            />
          </span> -->
        </div>
        </div>
        <number-chip
          :value="pendingUsersCount"
          :label="$t('common.pending_invites')"
          @click.native="modals.showPendingUsersModal = true"
          v-show="hasPendingUsers"
        />
      </div>
      <div class="user-table">
        <users-table
          :organizationId="organizationData.id"
          :organizationDataType="organizationData.type"
          :data="filteredOrganizationUsers"
          :row-class-name="addCustomStyle"
          @select-all="handleSelectRow"
          @select="handleSelectRow"
          @row-click="handleRowClick"
          :usersModifiers=usersModifiers
        />
      </div>
      <div class="pagination">
        <pagination
            :page-size="numberElementsPerPage"
            :total="usersInfo.count"
            :current-page.sync="currentPage"
            v-on:current-change="getOrganizationUsersPage"
            :pager-counter="pageCounter"
          />
        <over-table-pagination
          class="over-table-pagination-right"
          :currentPage="currentPage"
          :pageSize="numberElementsPerPage"
          :total="usersInfo.count"
        />
      </div>
    </div>
    <div class="modals">
      <disable-organization-user
        :dialogVisible="modals.showDisableAccountModal"
        v-on:closed="modals.showDisableAccountModal = false"
        :numberOfSelectedUsers="numberOfSelectedRows"
      />
      <delete-organization-user
        :dialogVisible="modals.showRemoveAccountModal"
        v-on:closed="modals.showRemoveAccountModal = false"
        :numberOfSelectedUsers="numberOfSelectedRows"
        @doRemoveStudentFromOrg="doRemoveStudentFromOrg"
      />
      <pending-invite-users
        :title="$t('common.pending_invites')"
        :dialogVisible="modals.showPendingUsersModal"
        @closed="modals.showPendingUsersModal = false"
        :pendingInviteUsers="pendingInviteUsersPage"
        :currentPage.sync="pendingInviteUsersCurrentPage"
        :total="pendingUsersCount"
        pendingInviteTypeScope="organization"
      />
    </div>
  </div>
</template>

<script>
  import DisableOrganizationUser from '@/components/modals/organizations/DisableOrganizationUser'
  import DeleteOrganizationUser from '@/components/modals/organizations/DeleteOrganizationUsers'
  import UsersTable from '@/components/common/UsersTable'
  import ChipIcon from '@/components/common/ChipIcon';
  import NumberChip from '@/components/common/NumberChip';
  import MoreOptionsPopover from '@/components/common/MoreOptionsPopover'
  import Pagination from '@/components/common/Pagination'
  import OverTablePagination from '@/components/common/OverTablePagination'
  import InviteUsers from '@/components/organizations/InviteUsers'
  import ElsaSearch from '@/components/common/Search'
  import PendingInviteUsers from '@/components/modals/PendingInviteUsers'
  import { NUMBER_ELEMENTS_PER_PAGE, PAGE_COUNTER } from "@/constants/generic"
  import {
    isEmpty,
    always,
    compose,
    pathOr,
    propOr,
    propSatisfies,
    not,
    length,
    isNil,
    either,
    pick,
    pluck,
    map,
    allPass,
    lt,
  } from 'ramda';
  import {
    mapActions,
    mapGetters,
  } from 'vuex'
  import {
    warningMessage,
    successMessage
  } from '../../utils/messages';

  export default {
    props: {
      organizationData: {
        type: Object,
        default: always({}),
      },
      usersModifiers: {
        type: String,
        default: null,
      }
    },
    data () {
      return {
        loading: false,
        currentPage: 1,
        pendingInviteUsersCurrentPage: 1,
        queryString: '',
        modals: {
          showInviteUsersModal: false,
          showImportUsersModal: false,
          showEditUserModal: false,
          showDisableAccountModal: false,
          showShareLinkModal: false,
          showRemoveAccountModal: false,
          showPendingUsersModal: false
        },
        organizationUserData: {},
        selectedOrganizationUsers: [],
      }
    },
    components: {
      InviteUsers,
      UsersTable,
      ChipIcon,
      MoreOptionsPopover,
      Pagination,
      OverTablePagination,
      DisableOrganizationUser,
      DeleteOrganizationUser,
      ElsaSearch,
      NumberChip,
      PendingInviteUsers
    },
    computed: {
      pageCounter() {
        return PAGE_COUNTER
      },
      numberElementsPerPage () {
        return NUMBER_ELEMENTS_PER_PAGE
      },
      filteredOrganizationUsers () {
        return this.users
      },
      hasNoUsers: allPass([
        compose(isEmpty, propOr([], 'users')),
        propSatisfies(either(isNil, isEmpty), 'usersModifiers'),
        propSatisfies(either(isNil, isEmpty), 'queryString'),
        propSatisfies(not, 'loading'),
      ]),
      showUserOptions: compose(not, isEmpty, propOr([], 'selectedOrganizationUsers')),
      numberOfSelectedRows: compose(length, propOr([], 'selectedOrganizationUsers')),
      getOrganizationClasses: compose(
        map(pick(['id', 'name'])),
        propOr([], 'getClassList')
      ),
      users: pathOr([], ['usersInfo', 'results']),
      nextPage: pathOr('', ['usersInfo', 'next']),
      previousPage: pathOr('', ['usersInfo', 'previous']),
      total: pathOr(0, ['usersInfo', 'count']),
      ...mapGetters('organization', {
        usersInfo: 'currentOrganizationUsers',
      }),
      ...mapGetters('classes', [
        'getClassList'
      ]),
      hasPendingUsers: compose(lt(0), propOr(0, 'pendingUsersCount')),
      ...mapGetters('organization', [
        'pendingUsersCount',
        'pendingInviteUsersPage',
      ])
    },
    methods: {
      onClickContinue(picked) {
        this.$emit('toggle-organization-nav', false)
        this.$emit('invite-users', picked)
      },
      handleRowClick(row, column){
        if(['action', 'roles', 'username'].includes(column.type)) {
          return;
        }
        const userId = row.id || 1;
        const organizationId = this.organizationData.id;
        this.$router.push({
          name: 'OrganizationUserProfile',
          params:{
            userId,
            organizationId,
          }
        })
      },
      handleAddGroupClick() {
        return this.$router.push({
          name: 'classes-new',
          params: {
            organizationId: this.organizationData.id
          }
        })
      },
      handleShareLinkClick () { this.modals.showShareLinkModal = true; },
      handleInviteUsersClick () { this.modals.showInviteUsersModal = true; },
      handleImportUsersClick () { this.modals.showImportUsersModal = true; },
      rowClicked (rowData) {
        this.modals.showEditUserModal = true;
        this.organizationUserData = rowData;
      },
      handleSelectRow(rowDat){
        this.selectedOrganizationUsers = rowDat
      },
      showRevokeLicense () {
        this.modals.showDisableAccountModal = true;
      },
      addCustomStyle({row}) {
        if(row.disabled) { return 'organization-user-disabled' }
        return ''
      },
      addToGroup(groupId) {
        const students = compose(pluck('email'), propOr([], 'selectedOrganizationUsers'))(this)
        try {
          this.addUsersToClass({
            students,
            groupId,
          })
          const message = this.$i18nLabelsHelper(this.organizationData.type, 'add_to_group_success')

          return successMessage(this.$t(message))(this)
        } catch(e) {
          const message = this.$i18nLabelsHelper(this.organizationData.type, 'add_to_group_warning')

          return warningMessage(this.$t(message))(this)
        }
      },

      doRemoveStudentFromOrg(isRemoveUserFromSystem = false) {
        const studentIds = compose(pluck('id'), propOr([], 'selectedOrganizationUsers'))(this)
        const organizationId = this.organizationData.id;
        const removeRequests = []
        const deleteUserFromSysRe = []
        studentIds.forEach(userId => {
          const removeUser = this.removeFromOrganization({userId, organizationId}).then(() => {
            if (isRemoveUserFromSystem) {
              this.removeUserFromSystem({studentId: userId});
            }
          })
          removeRequests.push(removeUser)
          
        });
        return Promise.all(removeRequests).then((resp) =>{
          const message = this.$i18nLabelsHelper(this.organizationData.type, 'remove_from_organization_success')
          successMessage(this.$t(message))(this);
          
          this.selectedOrganizationUsers = [];
          const search = this.queryString
          const roles = this.usersModifiers
          return this.getCurrentOrganizationUsers({
            organizationId: this.organizationData.id,
            roles,
            search
          });
        })
        .catch((error) => {
          const message = this.$i18nLabelsHelper(this.currentOrganizationType, 'remove_from_organization_warning')
          return warningMessage(this.$t(message))(this)
        })
      },
      handleManageGroupsClick() {},
      getOrganizationUsersPage (page) {
        this.loading = true
        const search = this.queryString
        const roles = this.usersModifiers
        return this.getCurrentOrganizationUsersPage({page, roles, search})
          .finally(() => this.loading = false)
      },
      searchUsers(search) {
        this.loading = true
        const roles = this.usersModifiers
        return this.getCurrentOrganizationUsers({
          roles,
          search,
        })
        .finally(() => this.loading = false)
      },
      ...mapActions('organization', [
        'getCurrentOrganizationUsers',
        'getCurrentOrganizationUsersPage',
        'getPendingInviteUsers',
        'removeFromOrganization'
      ]),
      ...mapActions('classes', [
        'addUsersToClass',
      ]),
      ...mapActions('user', [
        'removeUserFromSystem'
      ])
    },
    watch: {
      usersModifiers(newUsersModifiers) {
        this.loading = true
        const search = this.queryString
        const roles = newUsersModifiers
        this.getPendingInviteUsers({ roles })
        return this.getCurrentOrganizationUsers({
          organizationId: this.organizationData.id,
          roles,
          search
        })
        .finally(() => this.loading = false)
      },
      organizationData: {
        immediate: true,
        deep: true,
        handler() {
          this.loading = true
          const roles = this.usersModifiers
          const search = this.queryString

          this.getPendingInviteUsers({ roles })
          this.$store.dispatch('organization/getCurrentOrganizationUsers', {
            roles,
            search,
          })
          .finally(() => this.loading = false)

          return this.$store.dispatch('classes/getClasses')
        }
      },
      pendingInviteUsersCurrentPage(page) {
        const roles = this.usersModifiers
        return this.getPendingInviteUsers({ roles, page })
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "../../scss/_colors.scss";

  .actions {
    margin-top: 0px;
    margin-bottom: 0px;
    padding-top: 32px;
    padding-bottom: 40px;
    display: flex;
    justify-content: flex-start;

    &::v-deep .el-collapse {
      border: none;
      .el-collapse-item__header {
        min-width: 185px;
        height: 40px;
        background: $purple;
        border: 2px solid $purple;
        border-radius: 45px;
        font-weight: 600;
        font-size: 14px;
        color: white;
        text-align: center;

        .button {
          width: 100%;
          i {
            font-size: 18px;
            vertical-align: text-top;
            margin-right: 2px;
          }
        }
     }
      .el-collapse-item__wrap {
        position: absolute;
        width: 202px;
        z-index: 2;
        margin-top: 8px;
        box-shadow: 0px 4px 7px rgba(4, 33, 50, 0.1);
        padding: 24px;
      }
      .el-collapse-item__content {
        padding-bottom: 0;
      }
    }
  }

  .users-options {
    margin-left: 24px;
    display: flex;
    flex-direction: row;

    ::v-deep button {
      height: 100%;
      i.elsa-icon-class::before {
        width: 28px;
      }
    }
  }

  .options {
    display: flex;
    flex-direction: row;
    align-items: center;
    display: flex;
    justify-items: flex-start;
    height: 40px;
  }

  .actions .options ::v-deep .el-collapse .el-collapse-item__header {
    background: linear-gradient(180deg, #29F4FF -46.11%, #5257FF 151.97%);
    box-shadow: 0px 8px 18px rgba(69, 137, 255, 0.26);
    border-radius: 50px;
    font-weight: bold;
    font-size: 14px;
    color: white;
    padding-right: 10px;

    &.is-active {
      color: white;
      background-color: $purple;
    }
  }

  .search-users {
    margin-top: 32px;
    margin-bottom: 24px;

     &.el-input::v-deep .el-input__inner {
      padding-left: 16px;
    }
  }

  .action-item {
    display: flex;
    flex-direction: row;
    margin-top: auto;
    margin-bottom: auto;
    color: $purple;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    line-height: 22px;

    &:not(:last-of-type) {
      margin-bottom: 16px
    }

    &:hover {
      font-size: 14px;
      font-weight: 800;
    }

    i {
      border-width: 2px;
      height: 16px;
      width: 16px;
      padding-right: 8px;
      font-size: 16px;
      margin: auto 0;
    }
  }

  .no-users {
    padding-top: 20px;

    p {
      text-align: left;
      font-weight: 500;
      font-size: 18px;
      line-height: 22px;
      margin: 0;
      color: #6D7A8C;
    }

    .organization-actions {
      display: flex;
      flex-direction: row;

      .organization-action {
        margin: 0 16px 0 16px;
        background: #F9FBFD;
        &:active {
          border: 2px solid #B558FF;
          box-sizing: border-box;
        }
      }
    }
  }

  ::v-deep {
    .organization-user-disabled {
      opacity: 0.5;
    }
  }

  .user-list {
    display: flex;
    flex-direction: column;

    .over-table-pagination-right {
      align-self: flex-end;
    }
  }

  .el-form {
    &.search-form {
      margin-top: 48px;
    }
  }

  .options-wrapper {
    display: flex;
    height: fit-content;
  }

  .actions.divider {
    justify-content: space-between;
    align-items: center;
  }



</style>
